import { Ping } from '@fp/interfaces/conversational/generated-async/model/ping';
import { Subscribe } from '@fp/interfaces/conversational/generated-async/model/subscribe';
import { Publish } from '@fp/interfaces/conversational/generated-async/model/publish';
import { ActionPayload } from '@fp/interfaces/conversational/generated-async/model/action-payload';
import { ActionBodyPickChoice } from '@fp/interfaces/conversational/generated-async/model/action-body-pick-choice';
import { Action } from '@fp/interfaces/conversational/generated-async/model/action';
import { MessageOutgoing } from '@fp/interfaces/conversational/generated-async/model/message-outgoing';
import { MessageOutgoingPayload } from '@fp/interfaces/conversational/generated-async/model/message-outgoing-payload';
import { AttachChannel } from '@fp/interfaces/conversational/generated-async/model/attach-channel';
import { AttachChannelResponse } from '@fp/interfaces/conversational/generated-async/model/attach-channel-response';
import { Pong } from '@fp/interfaces/conversational/generated-async/model/pong';
import { MessageIncoming } from '@fp/interfaces/conversational/generated-async/model/message-incoming';
import { ServiceAttach } from '@fp/interfaces/conversational/generated-async/model/service-attach';
import { UI } from '@fp/interfaces/conversational/generated-async/model/ui';
import { Participant } from '@fp/interfaces/conversational/generated-async/model/participant';
import { StartConversation } from '@fp/interfaces/conversational/generated-async/model/start-conversation';
import { ActionBodyRead } from '@fp/interfaces/conversational/generated-async/model/action-body-read';
import { ActionBodyDismiss } from '@fp/interfaces/conversational/generated-async/model/action-body-dismiss';
import { ActionBodyDetachService } from '@fp/interfaces/conversational/generated-async/model/action-body-detach-service';
import { UIPayload } from '@fp/interfaces/conversational/generated-async/model/ui-payload';
import { UIBodyLoading } from '@fp/interfaces/conversational/generated-async/model/ui-body-loading';
import { ModelError } from '@fp/interfaces/conversational/generated-async/model/model-error';
import { ChatMessageModel } from '@klant/conversational-connector/models/ChatMessage.model';
import { ConversationModel } from '@klant/conversational-connector/models/Conversation.model';
import { ContextOutgoing } from '@fp/interfaces/conversational/generated-async/model/context-outgoing';
import { ChatModelMapper } from '@klant/conversational-connector/mappers/chat-model-mapper/chat-model.mapper';
import { WebsocketMapper } from '@klant/conversational-connector/mappers/websocket-mapper/websocket.mapper';
import { StartConversationResponse } from '@fp/interfaces/conversational/generated-async/model/start-conversation-response';

import { retry, RetryConfig, Subject } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Injectable, signal } from '@angular/core';

import {
    ActionModel,
    ConversationalChatConnectionError,
    MessageModel,
    Platform,
    WebsocketResponse
} from '../../klant-conversational-connector.model';

@Injectable({
    providedIn: 'root'
})
export class WebsocketService {
    public subject: WebSocketSubject<Subscribe | Publish>;
    public connectionErrorSubject = new Subject<ConversationalChatConnectionError>();
    public connectionSubjectResponse = new Subject<WebsocketResponse>();
    public messageLoading = signal<boolean>(false);

    private heartBeatTimer: number | null = null;
    private isAlive = false;
    private missedPongs = 0;
    private readonly MAX_MISSED_PONGS = 2;
    private readonly HEARTBEAT_INTERVAL = 30000;

    public retryConfig: RetryConfig = {
        delay: 2000
    };

    public connect(baseSocketUrl: string, openObserverNext: () => void): WebSocketSubject<any> {
        this.clearHeartBeat();

        this.subject = webSocket({
            url: baseSocketUrl,
            openObserver: {
                next: () => {
                    openObserverNext();
                    this.isAlive = true;
                    this.missedPongs = 0;
                    this.setHeartBeat();
                }
            },
            closeObserver: {
                next: this.websocketCloseErrorCheck.bind(this)
            }
        });

        return this.subject;
    }

    public setHeartBeat(): void {
        this.clearHeartBeat();

        this.subject.next({ type: Ping.TypeEnum.Ping });

        this.heartBeatTimer = window.setTimeout(() => {
            if (!this.isAlive) {
                this.missedPongs++;

                if (this.missedPongs >= this.MAX_MISSED_PONGS) {
                    this.clearHeartBeat();
                    this.connectionErrorSubject.next('connection error');
                    this.subject.complete();
                    return;
                }
            }

            this.isAlive = false;
            this.setHeartBeat();
        }, this.HEARTBEAT_INTERVAL);
    }

    public clearHeartBeat(): void {
        if (this.heartBeatTimer !== null) {
            window.clearTimeout(this.heartBeatTimer);
            this.heartBeatTimer = null;
        }
    }

    public websocketCloseErrorCheck(closeSocketEvent): void {
        this.clearHeartBeat();
        this.isAlive = false;

        if (closeSocketEvent.code === 1000 && closeSocketEvent.wasClean) {
            return;
        }

        if (closeSocketEvent.code > 1000 && closeSocketEvent.code <= 1015) {
            this.connectionErrorSubject.next('retry connection error');
            return;
        }

        this.connectionErrorSubject.next('connection error');
    }

    public attachSocketChannel(conversationResponse: ConversationModel): void {
        this.subject.next({
            type: 'attach_channel',
            payload: {
                accessToken: conversationResponse.accessToken,
                channelName: 'api'
            }
        } as AttachChannel);
    }

    public subscribeSocket(): void {
        this.subject.pipe(retry(this.retryConfig)).subscribe({
            next: (response: Subscribe) => {
                switch (response.type) {
                    case AttachChannelResponse.TypeEnum.AttachChannelResponse:
                        this.connectionSubjectResponse.next(WebsocketMapper.mapAttachChannelResponseToAttachChannelModel(response));
                        break;
                    case StartConversationResponse.TypeEnum.StartConversationResponse:
                        this.connectionSubjectResponse.next(WebsocketMapper.mapStartConversationResponseToStartConversationModel());
                        break;
                    case Pong.TypeEnum.Pong:
                        this.isAlive = true;
                        this.missedPongs = 0;
                        break;
                    case MessageIncoming.TypeEnum.Message:
                        const chatMessageModel = ChatModelMapper.mapMessageToChatMessageModel(response);
                        this.connectionSubjectResponse.next(chatMessageModel);
                        break;
                    case ServiceAttach.TypeEnum.ServiceAttach:
                        const serviceAttachModel = WebsocketMapper.mapServiceAttachToServiceAttachModel(response);
                        this.connectionSubjectResponse.next(serviceAttachModel);
                        break;
                    case UI.TypeEnum.Ui:
                        this.handleSocketUIEvents(response);
                        break;
                    case Participant.TypeEnum.Participant:
                        if (!response.payload.participant.introduction) {
                            break;
                        }
                        const participantModel = ChatModelMapper.mapParticipantPayloadToParticipantMessageModel(response.payload);
                        this.connectionSubjectResponse.next(participantModel);
                        break;
                    default:
                        if (response.type === ModelError.TypeEnum.Error) {
                            this.connectionErrorSubject.next('retry connection error');
                        }
                        break;
                }
            },
            error: () => {
                this.connectionErrorSubject.next('retry connection error');
            },
            complete: () => {
                this.isAlive = false;
            }
        });
    }

    public disconnect(): void {
        this.clearHeartBeat();
        if (this.subject) {
            this.subject.complete();
        }
    }

    public startConversation(): void {
        this.subject.next({ type: StartConversation.TypeEnum.StartConversation });
    }

    public sendMessage(payload: MessageModel): void {
        const messageToSend: MessageOutgoing = {
            type: MessageOutgoing.TypeEnum.Message,
            payload: {
                type: MessageOutgoingPayload.TypeEnum.Text,
                transactionId: payload.transactionId,
                body: {
                    text: payload.text
                }
            }
        };

        this.subject.next(messageToSend);
    }

    public markMessagesRead(messages: ChatMessageModel[]) {
        this.subject.next({
            type: Action.TypeEnum.Action,
            payload: {
                type: ActionBodyRead.TypeEnum.Read,
                events: messages.map((message) => message.id)
            }
        });
    }

    public sendPickChoiceAction(action: ActionModel): void {
        const payload: ActionPayload = {
            type: ActionBodyPickChoice.TypeEnum.PickChoice,
            originMessage: action.originMessage,
            transactionId: action.transactionId,
            choice: {
                chooseAgain: false,
                id: action.choiceOptionId
            }
        };

        this.subject.next({
            type: Action.TypeEnum.Action,
            payload
        });
    }

    public continueConversationAfterIdle(): void {
        this.subject.next({
            type: Action.TypeEnum.Action,
            payload: {
                type: ActionBodyDismiss.TypeEnum.Dismiss,
                body: {
                    type: 'resume_conversation_prompt'
                }
            }
        });
    }

    public endConversationAfterIdle(): void {
        this.subject.next({
            type: Action.TypeEnum.Action,
            payload: {
                type: ActionBodyDetachService.TypeEnum.DetachService
            }
        });
    }

    public setSeamlyContextVariables(platform: Platform, environment: string): void {
        this.subject.next({
            type: ContextOutgoing.TypeEnum.Context,
            payload: {
                variables: {
                    channel: platform,
                    environment: environment
                }
            }
        });
    }

    private handleSocketUIEvents(uiEvent: UI): void {
        if (uiEvent.payload.type === UIPayload.TypeEnum.Loading) {
            this.messageLoading.set((uiEvent.payload.body as UIBodyLoading).loading);
        }
    }
}
